/**
 * DocumentVersionPanel - Phase 2 Document versioning and history
 * Allows upload new versions, view history, download specific versions
 */

import React, { useState, useEffect } from 'react';
import {
  FileText, Upload, Download, History, ChevronDown, ChevronRight,
  Plus, Trash2, X, Clock, User, FileCheck, ExternalLink, RefreshCw
} from 'lucide-react';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useProductMode } from '../context/ProductModeContext';
import { toast } from 'sonner';
import { trackEvent, TELEMETRY_EVENTS } from '../utils/telemetry';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

/**
 * Version History Modal
 */
const VersionHistoryModal = ({ isOpen, onClose, doc, scopeId, tenantId, onVersionDownload }) => {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    if (isOpen && doc) {
      loadVersions();
    }
  }, [isOpen, doc]);
  
  const loadVersions = async () => {
    setLoading(true);
    try {
      const res = await api.getDocumentVersions(scopeId, doc.id, tenantId);
      setVersions(res.data.versions || []);
      trackEvent(TELEMETRY_EVENTS.SCOPING_DOC_VERSION_VIEWED, { scopeId, docId: doc.id });
    } catch (err) {
      console.error('Failed to load versions:', err);
      // Fall back to doc.versions if available
      setVersions(doc.versions || []);
    } finally {
      setLoading(false);
    }
  };
  
  const handleDownload = (version) => {
    trackEvent(TELEMETRY_EVENTS.SCOPING_DOC_VERSION_DOWNLOADED, {
      scopeId,
      docId: doc.id,
      version: version.version
    });
    window.open(version.url, '_blank');
    onVersionDownload?.(version);
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-5 h-5 text-[var(--clr-brand-primary)]" />
            Version History: {doc?.name}
          </DialogTitle>
        </DialogHeader>
        
        <div className="mt-4 space-y-2 max-h-96 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-5 h-5 text-[var(--clr-text-muted)] animate-spin" />
            </div>
          ) : versions.length === 0 ? (
            <p className="text-sm text-[var(--clr-text-muted)] text-center py-4">
              No version history available
            </p>
          ) : (
            versions.map((version, idx) => (
              <div
                key={version.version}
                className={`flex items-center justify-between p-3 rounded-lg border ${
                  idx === 0 
                    ? 'bg-[var(--clr-brand-primary)]/5 border-[var(--clr-brand-primary)]/20' 
                    : 'bg-[var(--clr-surface-card)] border-[var(--clr-border-light)]'
                }`}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">
                      Version {version.version}
                    </span>
                    {idx === 0 && (
                      <span className="px-1.5 py-0.5 text-xs bg-[var(--clr-brand-primary)] text-white rounded">
                        Current
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-xs text-[var(--clr-text-muted)]">
                    <span className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      {version.uploaded_by}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {new Date(version.uploaded_at).toLocaleString()}
                    </span>
                    {version.size_bytes && (
                      <span>{Math.round(version.size_bytes / 1024)}KB</span>
                    )}
                  </div>
                  {version.notes && (
                    <p className="text-xs text-[var(--clr-text-secondary)] mt-1 italic">
                      "{version.notes}"
                    </p>
                  )}
                </div>
                <button
                  onClick={() => handleDownload(version)}
                  className="p-2 text-[var(--clr-text-muted)] hover:text-[var(--clr-brand-primary)] hover:bg-[var(--clr-surface-hover)] rounded-lg transition-colors"
                  title="Download this version"
                >
                  <Download className="w-4 h-4" />
                </button>
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Upload New Version Modal
 */
const UploadVersionModal = ({ isOpen, onClose, doc, scopeId, tenantId, onSuccess }) => {
  const { user } = useAuth();
  const [url, setUrl] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  
  const handleUpload = async () => {
    if (!url.trim()) {
      toast.error('URL is required');
      return;
    }
    
    setLoading(true);
    try {
      await api.uploadDocumentVersion(
        scopeId,
        {
          docId: doc.id,
          name: doc.name,
          url: url.trim(),
          type: doc.type,
          notes: notes.trim() || undefined,
          replace: true
        },
        tenantId,
        user?.id || user?._id,
        user?.name || user?.email
      );
      
      trackEvent(TELEMETRY_EVENTS.SCOPING_DOC_VERSION_UPLOADED, {
        scopeId,
        docId: doc.id
      });
      
      toast.success('New version uploaded');
      onSuccess?.();
      onClose();
      setUrl('');
      setNotes('');
    } catch (err) {
      toast.error('Failed to upload version');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5 text-[var(--clr-brand-primary)]" />
            Upload New Version
          </DialogTitle>
        </DialogHeader>
        
        <div className="mt-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-[var(--clr-text-primary)] mb-1">
              Document: {doc?.name}
            </label>
            <p className="text-xs text-[var(--clr-text-muted)]">
              Current version: V{doc?.current_version || 1}
            </p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-[var(--clr-text-primary)] mb-1">
              New File URL *
            </label>
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://..."
              className="w-full px-3 py-2 text-sm border border-[var(--clr-border-base)] rounded-lg focus:ring-2 focus:ring-[var(--clr-brand-primary)]/20 focus:border-[var(--clr-brand-primary)]"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-[var(--clr-text-primary)] mb-1">
              Version Notes (optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="What changed in this version..."
              rows={2}
              className="w-full px-3 py-2 text-sm border border-[var(--clr-border-base)] rounded-lg focus:ring-2 focus:ring-[var(--clr-brand-primary)]/20 focus:border-[var(--clr-brand-primary)]"
            />
          </div>
          
          <div className="flex gap-2 pt-2">
            <button
              onClick={handleUpload}
              disabled={loading || !url.trim()}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-[var(--clr-brand-primary)] text-white rounded-lg hover:bg-[var(--clr-brand-primary-hover)] disabled:opacity-50 transition-colors"
            >
              {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              Upload V{(doc?.current_version || 1) + 1}
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 text-[var(--clr-text-muted)] hover:bg-[var(--clr-surface-hover)] rounded-lg transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Document Version Panel - Main Component
 */
const DocumentVersionPanel = ({ scopeId, tenantId, canEdit = true }) => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);
  const [historyDoc, setHistoryDoc] = useState(null);
  const [uploadDoc, setUploadDoc] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newDoc, setNewDoc] = useState({ name: '', url: '', type: 'url', notes: '' });
  const { user } = useAuth();
  const { getFeature } = useProductMode();
  
  const docVersioningEnabled = getFeature('scoping.docVersioning', false);
  
  useEffect(() => {
    if (scopeId && tenantId) {
      loadDocs();
    }
  }, [scopeId, tenantId]);
  
  const loadDocs = async () => {
    setLoading(true);
    try {
      if (docVersioningEnabled) {
        const res = await api.getVersionedDocuments(scopeId, tenantId);
        setDocs(res.data || []);
      } else {
        const res = await api.getScopeDocuments(scopeId, tenantId);
        setDocs((res.data || []).map(d => ({ ...d, current_version: 1, versions: [] })));
      }
    } catch (err) {
      console.error('Failed to load documents:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const handleAddDocument = async () => {
    if (!newDoc.name || !newDoc.url) {
      toast.error('Name and URL are required');
      return;
    }
    
    try {
      const docId = `doc_${Date.now()}`;
      await api.uploadDocumentVersion(
        scopeId,
        {
          docId,
          name: newDoc.name,
          url: newDoc.url,
          type: newDoc.type,
          notes: newDoc.notes || 'Initial upload',
          replace: false
        },
        tenantId,
        user?.id || user?._id,
        user?.name || user?.email
      );
      
      trackEvent(TELEMETRY_EVENTS.SCOPING_DOC_ATTACHED, { scopeId });
      toast.success('Document attached');
      setNewDoc({ name: '', url: '', type: 'url', notes: '' });
      setShowAddForm(false);
      loadDocs();
    } catch (err) {
      toast.error('Failed to attach document');
    }
  };
  
  const handleDownload = (doc) => {
    trackEvent(TELEMETRY_EVENTS.SCOPING_DOC_DOWNLOADED, { scopeId, docId: doc.id });
    window.open(doc.url, '_blank');
  };
  
  return (
    <div className="border border-[var(--clr-border-light)] rounded-lg overflow-hidden" data-testid="document-version-panel">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-3 bg-[var(--clr-surface-subtle)] hover:bg-[var(--clr-surface-hover)] transition-colors"
      >
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-[var(--clr-text-muted)]" />
          <span className="text-sm font-medium">Documents</span>
          {docs.length > 0 && (
            <span className="px-1.5 py-0.5 text-xs bg-gray-100 text-gray-700 rounded-full">
              {docs.length}
            </span>
          )}
          {docVersioningEnabled && (
            <span className="px-1.5 py-0.5 text-xs bg-purple-100 text-purple-700 rounded-full">
              Versioned
            </span>
          )}
        </div>
        {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
      </button>
      
      {expanded && (
        <div className="p-3 space-y-2">
          {loading ? (
            <div className="flex items-center justify-center py-4">
              <RefreshCw className="w-4 h-4 text-[var(--clr-text-muted)] animate-spin" />
            </div>
          ) : docs.length === 0 && !showAddForm ? (
            <p className="text-xs text-[var(--clr-text-muted)] text-center py-2">
              No documents attached
            </p>
          ) : (
            docs.map((doc) => (
              <div
                key={doc.id}
                className="flex items-center justify-between p-2 bg-[var(--clr-surface-card)] rounded-lg border border-[var(--clr-border-light)]"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-[var(--clr-text-primary)] truncate">
                      {doc.name}
                    </p>
                    {docVersioningEnabled && doc.current_version > 1 && (
                      <span className="px-1 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">
                        V{doc.current_version}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-[var(--clr-text-muted)]">
                    {doc.uploaded_by} • {new Date(doc.uploaded_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  {docVersioningEnabled && (
                    <>
                      <button
                        onClick={() => setHistoryDoc(doc)}
                        className="p-1.5 text-[var(--clr-text-muted)] hover:text-purple-600 hover:bg-purple-50 rounded transition-colors"
                        title="View history"
                      >
                        <History className="w-4 h-4" />
                      </button>
                      {canEdit && (
                        <button
                          onClick={() => setUploadDoc(doc)}
                          className="p-1.5 text-[var(--clr-text-muted)] hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                          title="Upload new version"
                        >
                          <Upload className="w-4 h-4" />
                        </button>
                      )}
                    </>
                  )}
                  <button
                    onClick={() => handleDownload(doc)}
                    className="p-1.5 text-[var(--clr-text-muted)] hover:text-[var(--clr-brand-primary)] hover:bg-[var(--clr-surface-hover)] rounded transition-colors"
                    title="Download/Open"
                  >
                    <Download className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))
          )}
          
          {showAddForm ? (
            <div className="p-3 bg-[var(--clr-surface-subtle)] rounded-lg space-y-2">
              <input
                type="text"
                placeholder="Document name"
                value={newDoc.name}
                onChange={(e) => setNewDoc({ ...newDoc, name: e.target.value })}
                className="w-full px-3 py-2 text-sm border rounded-lg"
              />
              <input
                type="url"
                placeholder="URL or file link"
                value={newDoc.url}
                onChange={(e) => setNewDoc({ ...newDoc, url: e.target.value })}
                className="w-full px-3 py-2 text-sm border rounded-lg"
              />
              <input
                type="text"
                placeholder="Notes (optional)"
                value={newDoc.notes}
                onChange={(e) => setNewDoc({ ...newDoc, notes: e.target.value })}
                className="w-full px-3 py-2 text-sm border rounded-lg"
              />
              <div className="flex gap-2">
                <button
                  onClick={handleAddDocument}
                  className="flex-1 px-3 py-2 text-sm bg-[var(--clr-brand-primary)] text-white rounded-lg hover:bg-[var(--clr-brand-primary-hover)]"
                >
                  Attach
                </button>
                <button
                  onClick={() => setShowAddForm(false)}
                  className="px-3 py-2 text-sm text-[var(--clr-text-muted)] hover:bg-[var(--clr-surface-hover)] rounded-lg"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : canEdit && (
            <button
              onClick={() => setShowAddForm(true)}
              className="w-full flex items-center justify-center gap-2 p-2 text-sm text-[var(--clr-brand-primary)] hover:bg-[var(--clr-surface-hover)] rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
              Attach Document
            </button>
          )}
        </div>
      )}
      
      {/* Modals */}
      <VersionHistoryModal
        isOpen={!!historyDoc}
        onClose={() => setHistoryDoc(null)}
        doc={historyDoc}
        scopeId={scopeId}
        tenantId={tenantId}
      />
      
      <UploadVersionModal
        isOpen={!!uploadDoc}
        onClose={() => setUploadDoc(null)}
        doc={uploadDoc}
        scopeId={scopeId}
        tenantId={tenantId}
        onSuccess={loadDocs}
      />
    </div>
  );
};

export default DocumentVersionPanel;
export { VersionHistoryModal, UploadVersionModal };
